import { useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Utilities to assist scrolling a parent so that it snaps neatly to an appropriate child.

/**
 * @typedef ItemPositionPosition
 * @property {number} [ItemPositionItem.start] - optional theme scale variants e.g. compact, wide
 * @property {number} [ItemPositionItem.end] - optional override to force a particular pixel size
 *
 * @typedef {Object.<number, ItemPositionItem>} ItemPositionPositions - ref object of start/end coordinates
 *
 * @typedef ItemPositions
 * @property {number} ItemPositions.contentWidth - current width of potentially-overflowing content inside scrollable
 * @property {number} ItemPositions.containerWidth - current width of non-overflowing outer scroll container
 * @property {number} ItemPositions.scrollOffset - current x position of the left edge of the visible section of the scrollable content
 * @property {ItemPositionPositions} ItemPositions.positions - `{ start, end }` coordinates of each scrollable content item, by index
 * @property {(targetX: number) => void)} ItemPositions.scrollTo - function to scroll to a co-ordinate, applying scrolling logic
 * @property {() => void)} ItemPositions.setIsReady - function to call when layout has been calculated and itemPositions data is ready
 */

/**
 * Takes a desired scroll target coordinate and returns a valid coordinate
 * that, when not scrolling to one end or another, snaps to the start of an item,
 * making sure any "scroll back" button doesn't overlap the first item.
 *
 * @param {number} x - the intended coordinate to scroll to
 * @param {number} scrollMax - the highest posible coordinate to scroll to
 * @param {ItemPositionPositions} positions - positions list from `useItemPositions` ref
 * @param {number} [buttonClearance] - offset to allow space for a button
 * @returns {number}
 */
export const getItemPositionScrollTarget = function (x, scrollMax, positions) {
  let buttonClearance = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  if (x <= 0) return 0;
  if (x >= scrollMax) return scrollMax;

  // If not scrolling all the way to the start or end, stop
  // where the first item is not coverred by the scroll previous button
  const targetItem = Object.values(positions).find(_ref => {
    let {
      start,
      end
    } = _ref;
    return start <= x && end > x;
  });

  // If there's no target item we're landing at a gap between items
  const targetX = targetItem ? targetItem.start : x;
  return Math.max(0, targetX - buttonClearance);
};

/**
 * Returns a handler for React Native's `onLayout` which updates the values of
 * the ref object returned from `useItemPositions`.
 *
 * @param {ItemPositionPositions} positions
 * @param {number} index
 * @returns {function}
 */
export const getItemPositionLayoutHandler = (positions, index) => {
  const handleLayout = _ref2 => {
    let {
      nativeEvent: {
        layout: {
          width,
          x
        }
      }
    } = _ref2;
    /* eslint-disable-next-line no-param-reassign */
    positions[index] = {
      start: x,
      end: x + width
    };
  };
  return handleLayout;
};

/**
 * Wrapper for `useRef` which gives a mutable object in which item start/end coordinates,
 * scrollTo method and content/container widths may be set and updated without causing a re-render.
 *
 * @returns {[ItemPositions, isReady: bool]} tuple array of:
 *  - current ref object `{ positions, contentWidth, containerWidth, scrollOffset, scrollTo, setIsReady }`
 *  - boolean that updates to `true` (causing a re-render) after the first time `setIsReady` is called
 */
export const useItemPositions = () => {
  const [isReady, setIsReady] = useState(false);
  return [useRef({
    positions: {},
    scrollTo: () => {},
    contentWidth: 0,
    containerWidth: 0,
    scrollOffset: 0,
    setIsReady
  }).current, isReady];
};

/**
 * PropType for the ref object returned by `useItemPositions`
 */
export const itemPositionsPropType = PropTypes.exact({
  positions: PropTypes.objectOf(PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number
  })),
  scrollTo: PropTypes.func,
  contentWidth: PropTypes.number,
  containerWidth: PropTypes.number,
  scrollOffset: PropTypes.number,
  setIsReady: PropTypes.func
});